import React, { useState, useMemo, useCallback } from 'react';
import { Box, Alert, IconButton, Badge, Tooltip, CircularProgress } from '@mui/material';
import { FilterList as FilterIcon, Refresh as RefreshIcon } from '@mui/icons-material';

import { doctorShiftRepository } from '../../../../infrastructure/repositories/DoctorShiftRepositoryImpl';

import {
  useMonthlyDoctorsShift,
  useUrlState,
  useShiftFilter,
  useShiftSort,
} from '../hooks';

import {
  NavigationControls,
  TimelineView,
  SlotSummaryTable,
  ShiftEditPopup,
  FilterDrawer,
  SortOptions,
} from '../components';

import {
  PageContainer,
  HeaderSection,
  NavigationArea,
  ActionArea,
  ErrorContainer,
} from '../MonthlyDoctorsShift.styles';

/**
 * MonthlyDoctorsShiftContainerコンポーネント
 *
 * @description
 * 月次医師シフト管理画面のコンテナコンポーネント。
 * フックとコンポーネントを統合し、状態管理とイベントハンドリングを行う。
 *
 * @returns {React.ReactElement} コンテナコンポーネント
 */
const MonthlyDoctorsShiftContainer: React.FC = () => {
  // URL状態管理
  const { params, setDate, setViewMode } = useUrlState();

  // メインフック
  const {
    shifts,
    isLoading,
    error,
    editingShift,
    isPopupOpen,
    isCreating,
    fetchShifts,
    getDatesInRange,
    getNextDate,
    getPreviousDate,
    handleShiftClick,
    handleTimelineDoubleClick,
    closePopup,
    saveShift,
    deleteShift,
    clearError,
  } = useMonthlyDoctorsShift(doctorShiftRepository, params.date, params.viewMode);

  // フィルターフック
  const {
    filter,
    isFiltering,
    setDoctorKeyword,
    setTimeFrom,
    setTimeTo,
    resetFilter,
    filterShifts,
  } = useShiftFilter();

  // ソートフック
  const { sortState, setSortBy, toggleSortOrder, sortShifts } = useShiftSort();

  // フィルタードロワーの状態
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // フィルター・ソート適用後のシフト
  const processedShifts = useMemo(() => {
    const filtered = filterShifts(shifts);
    return sortShifts(filtered);
  }, [shifts, filterShifts, sortShifts]);

  // 表示期間の日付配列
  const datesInRange = useMemo(
    () => getDatesInRange(params.date, params.viewMode),
    [getDatesInRange, params.date, params.viewMode]
  );


  /**
   * 次の期間へ移動
   */
  const handleNext = useCallback(() => {
    const nextDate = getNextDate(params.date, params.viewMode);
    setDate(nextDate);
  }, [getNextDate, params.date, params.viewMode, setDate]);

  /**
   * 前の期間へ移動
   */
  const handlePrevious = useCallback(() => {
    const prevDate = getPreviousDate(params.date, params.viewMode);
    setDate(prevDate);
  }, [getPreviousDate, params.date, params.viewMode, setDate]);

  /**
   * 今日へ移動
   */
  const handleToday = useCallback(() => {
    setDate(new Date());
  }, [setDate]);

  /**
   * フィルタードロワーを開く
   */
  const handleOpenFilterDrawer = useCallback(() => {
    setIsFilterDrawerOpen(true);
  }, []);

  /**
   * フィルタードロワーを閉じる
   */
  const handleCloseFilterDrawer = useCallback(() => {
    setIsFilterDrawerOpen(false);
  }, []);

  /**
   * 枠集計ビューかどうか判定
   */
  const isSlotSummaryView =
    params.viewMode === 'slotSummaryWeek' || params.viewMode === 'slotSummaryMonth';

  return (
    <PageContainer>
      {/* エラー表示 */}
      {error && (
        <ErrorContainer>
          <Alert severity="error" onClose={clearError}>
            {error}
          </Alert>
        </ErrorContainer>
      )}

      {/* ヘッダーセクション */}
      <HeaderSection>
        {/* ナビゲーションエリア */}
        <NavigationArea>
          <NavigationControls
            selectedDate={params.date}
            viewMode={params.viewMode}
            onDateChange={setDate}
            onViewModeChange={setViewMode}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onToday={handleToday}
          />
        </NavigationArea>

        {/* アクションエリア */}
        <ActionArea>
          {/* ソートオプション */}
          {!isSlotSummaryView && (
            <SortOptions
              sortBy={sortState.sortBy}
              sortOrder={sortState.sortOrder}
              onSortByChange={setSortBy}
              onSortOrderToggle={toggleSortOrder}
            />
          )}

          {/* フィルターボタン */}
          <Tooltip title="絞り込み">
            <IconButton onClick={handleOpenFilterDrawer}>
              <Badge
                color="primary"
                variant="dot"
                invisible={!isFiltering}
              >
                <FilterIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* 更新ボタン */}
          <Tooltip title="更新">
            <IconButton onClick={fetchShifts} disabled={isLoading}>
              {isLoading ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </ActionArea>
      </HeaderSection>

      {/* メインコンテンツ */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {isSlotSummaryView ? (
          /* 枠集計テーブルビュー */
          <SlotSummaryTable
            shifts={processedShifts}
            dates={datesInRange}
            mode={params.viewMode === 'slotSummaryWeek' ? 'week' : 'month'}
          />
        ) : (
          /* タイムラインビュー */
          <TimelineView
            shifts={processedShifts}
            dates={datesInRange}
            onShiftClick={handleShiftClick}
            onDoubleClick={handleTimelineDoubleClick}
          />
        )}
      </Box>

      {/* シフト編集ポップアップ */}
      <ShiftEditPopup
        open={isPopupOpen}
        shift={editingShift}
        isCreating={isCreating}
        isLoading={isLoading}
        error={error}
        onSave={saveShift}
        onDelete={deleteShift}
        onCancel={closePopup}
      />

      {/* フィルタードロワー */}
      <FilterDrawer
        open={isFilterDrawerOpen}
        onClose={handleCloseFilterDrawer}
        filter={filter}
        isFiltering={isFiltering}
        onDoctorKeywordChange={setDoctorKeyword}
        onTimeFromChange={setTimeFrom}
        onTimeToChange={setTimeTo}
        onReset={resetFilter}
      />
    </PageContainer>
  );
};

export { MonthlyDoctorsShiftContainer };
